import { useEffect, useRef, useState } from 'react';
import { App, Button, Drawer, Input, Tag } from 'antd';
import { PlayCircleOutlined, StopOutlined } from '@ant-design/icons';

interface SseEvent {
  id: string;
  eventType: string;
  data: string;
  timestamp: string;
  isSystem?: boolean;
}

type SseStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

const STATUS_COLOR: Record<SseStatus, string> = {
  idle: 'default',
  connecting: 'processing',
  open: 'success',
  closed: 'default',
  error: 'error',
};

interface SseDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * A real Server-Sent Events client using the browser's native EventSource
 * API — the connection is made directly by the browser, same as the
 * WebSocket client, so no backend proxying is needed or used here.
 */
export const SseDrawer = ({ open, onClose }: SseDrawerProps) => {
  const { message } = App.useApp();
  const [url, setUrl] = useState('');
  const [eventNames, setEventNames] = useState('');
  const [withCredentials, setWithCredentials] = useState(false);
  const [status, setStatus] = useState<SseStatus>('idle');
  const [events, setEvents] = useState<SseEvent[]>([]);
  const sourceRef = useRef<EventSource | null>(null);
  const logRef = useRef<HTMLDivElement>(null);

  const addEvent = (eventType: string, data: string, isSystem = false) => {
    setEvents((prev) => [
      ...prev,
      { id: crypto.randomUUID(), eventType, data, timestamp: new Date().toISOString(), isSystem },
    ]);
  };

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [events]);

  useEffect(() => () => sourceRef.current?.close(), []);

  const connect = () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      void message.error('Enter a stream URL first');
      return;
    }

    setStatus('connecting');
    setEvents([]);
    addEvent('system', `Connecting to ${trimmedUrl}…`, true);

    const source = new EventSource(trimmedUrl, { withCredentials });
    sourceRef.current = source;

    source.onopen = () => {
      setStatus('open');
      addEvent('system', 'Connected', true);
    };
    source.onmessage = (event) => {
      addEvent('message', event.data as string);
    };
    source.onerror = () => {
      setStatus(source.readyState === EventSource.CLOSED ? 'closed' : 'error');
      addEvent(
        'system',
        'Connection error (the browser will retry automatically unless closed)',
        true,
      );
    };

    for (const name of eventNames
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean)) {
      source.addEventListener(name, (event) => {
        addEvent(name, (event as MessageEvent).data as string);
      });
    }
  };

  const disconnect = () => {
    sourceRef.current?.close();
    setStatus('closed');
    addEvent('system', 'Disconnected', true);
    sourceRef.current = null;
  };

  const isConnected = status === 'open' || status === 'connecting';

  return (
    <Drawer title="Server-Sent Events Client" open={open} onClose={onClose} size={720}>
      <div className="flex h-full flex-col gap-3">
        <div className="flex items-center gap-2">
          <Tag color={STATUS_COLOR[status]} className="shrink-0 capitalize">
            {status}
          </Tag>
          <Input
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://example.com/events"
            disabled={isConnected}
            className="flex-1 font-mono"
          />
          {isConnected ? (
            <Button danger icon={<StopOutlined />} onClick={disconnect}>
              Disconnect
            </Button>
          ) : (
            <Button type="primary" icon={<PlayCircleOutlined />} onClick={connect}>
              Connect
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Input
            value={eventNames}
            onChange={(event) => setEventNames(event.target.value)}
            placeholder="Named events to listen for (comma-separated, optional — default 'message' always listened)"
            disabled={isConnected}
            size="small"
            className="flex-1"
          />
          <label className="flex shrink-0 items-center gap-1.5 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={withCredentials}
              onChange={(event) => setWithCredentials(event.target.checked)}
              disabled={isConnected}
            />
            Send credentials
          </label>
        </div>

        <div
          ref={logRef}
          className="flex-1 overflow-y-auto rounded border border-black/10 p-2 font-mono text-xs dark:border-white/10"
        >
          {events.length === 0 && (
            <span className="text-gray-400">No events yet — connect to a stream.</span>
          )}
          {events.map((evt) => (
            <div
              key={evt.id}
              className={`mb-1 ${evt.isSystem ? 'text-gray-400' : 'text-emerald-400'}`}
            >
              <span className="mr-2 text-gray-500">
                {new Date(evt.timestamp).toLocaleTimeString()}
              </span>
              {!evt.isSystem && (
                <span className="mr-1.5 rounded bg-emerald-500/10 px-1 text-[10px] text-emerald-400">
                  {evt.eventType}
                </span>
              )}
              <span className="whitespace-pre-wrap break-all">{evt.data}</span>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
};
